import React, { Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

// Components
import Header from "./components/Header/Header";
import Footer from "./components/Footer/Footer";
import MainNavigation from "./components/Header/Navigation";
import ErrorBoundary from "./components/ErrorBoundary";
import LoadingSpinner from "./components/UI/LoadingSpinner";
import { ToastProvider } from "./components/contexts/ToastContext";
import CookieConsent from "./components/CookieConsent";

// Authentication Components
import Login from "./components/auth/Login";
import Register from "./components/auth/Register";
import ForgotPassword from "./components/auth/ForgotPassword";
import AuthCallback from "./components/auth/AuthCallback";
import Profile from "./components/auth/Profile";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminProtectedRoute from "./components/AdminProtectedRoute";
import Unauthorized from "./components/Unauthorized";
import ConnectionHelp from "./pages/ConnectionHelp";

// Auth Context
import { AuthProvider } from "./contexts/AuthContext";

// Pages - Communs
import Home from "./pages/Home/Home";
import Contact from "./pages/Contact/Contact";
import Partenaires from "./pages/Partenaires/Partenaires";
import ActuCfaPublic from "./pages/ActuCfaPublic";

// Pages - Pôle Auto
import CalendrierMecanique from "./pages/PoleAuto/Calendrier/CalendrierMecanique";
import GestionMateriel from './pages/PoleAuto/GestionMateriel';
import ApplicationGarage from "./pages/PoleAuto/Application/ApplicationGarage";
import ActualitesAuto from "./pages/PoleAuto/Actualites/ActualitesAuto";
import ConnexionAuto from "./pages/PoleAuto/Connexion/ConnexionAuto";
import Calendar from "./pages/PoleAuto/Calendrier/Calendar";
import EmargementCQP from "./pages/PoleAuto/EmargementCQP/Emargement";
import ClientForm from "./pages/PoleAuto/Application/ClientForm";
import InterventionForm from "./pages/PoleAuto/Application/InterventionForm";
import ToutesFormations from "./components/Admin/ToutesFormations";
import StatistiquesClients from "./components/Admin/StatistiquesClients";
import InterventionsList from './pages/PoleAuto/InterventionsList';

// Pages - Pôle Vente
import WordPress from "./pages/PoleVente/WordPress/WordPress";
import PrestaShop from "./pages/PoleVente/PrestaShop/PrestaShop";
import ActualitesVente from "./pages/PoleVente/Actualites/ActualitesVente";

// Pages - Mentions légales
import Mentions from "./pages/Legal/Mentions";
import RGPD from "./pages/Legal/RGPD";
import Conditions from "./pages/Legal/Conditions";

// Pages - Home Formations
import Beaute from "./pages/Home/BeauteBienEtre";
import Vente from "./pages/Home/VenteGestion";
import Services from "./pages/Home/ServicesPersonne";
import Auto from "./pages/Home/Automobile";
import Sup from "./pages/Home/SuperieurVente";
import GF from "./pages/Home/GreenFactory";

// Pages - Admin - Nouveau système
import AdminLayout from "./components/Admin/AdminLayout";
import DashboardAdmin from "./pages/admin/DashboardAdmin";
import ArticlesPage from "./pages/admin/ArticlesPage";
import CompetencesPage from "./pages/admin/CompetencesPage";
import FormationsPage from "./pages/admin/FormationsPage";
import ApprenantsPage from "./pages/admin/ApprenantsPage";
import StatistiquesPage from "./pages/admin/StatistiquesPage";
import PDFViewer from "./components/Admin/PDFViewer";
import RolesPage from "./pages/admin/RolesPage";
import ParametresPage from "./pages/admin/ParametresPage";

// Composant standalone pour la page actualités (SANS Header/Nav/Footer)
function StandaloneActualites() {
  console.log("🎨 Page Actualités Standalone rendue");
  return (
    <div className="min-h-screen">
      <ActuCfaPublic />
      <CookieConsent />
    </div>
  );
}

// Composant avec layout complet (Header + Nav + Footer)
function AppContentWithLayout() {
  console.log("📱 AppContent avec layout rendu");
  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <Header />
      <MainNavigation />

      <main className="flex-grow">
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Routes publiques */}
            <Route path="/" element={<Home />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/partenaires" element={<Partenaires />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="/connexion-help" element={<ConnectionHelp />} />
            <Route path="/beaute-bien-etre" element={<Beaute />} />
            <Route path="/vente-gestion" element={<Vente />} />
            <Route path="/services-personne" element={<Services />} />
            <Route path="/automobile" element={<Auto />} />
            <Route path="/superieur-vente" element={<Sup />} />
            <Route path="/green-factory" element={<GF />} />
            
            {/* Routes pour les pages légales */}
            <Route path="/legal">
              <Route path="mentions" element={<Mentions />} />
              <Route path="rgpd" element={<RGPD />} />
              <Route path="conditions" element={<Conditions />} />
            </Route>

            {/* Routes Pôle Auto publiques */}
            <Route path="/pole-auto">
              <Route index element={<ActualitesAuto />} />
              <Route path="actualites" element={<ActualitesAuto />} />
              <Route path="connexion" element={<ConnexionAuto />} />
            </Route>

            {/* Routes Pôle Vente publiques */}
            <Route path="/pole-vente">
              <Route index element={<ActualitesVente />} />
              <Route path="actualites" element={<ActualitesVente />} />
              <Route path="wordpress" element={<WordPress />} />
              <Route path="prestashop" element={<PrestaShop />} />
            </Route>

            {/* Routes protégées (utilisateurs authentifiés) */}
            <Route element={<ProtectedRoute />}>
              <Route path="/profile" element={<Profile />} />
              <Route
                path="/pole-auto/calendrier"
                element={<CalendrierMecanique />}
              />
              <Route path="/pole-auto/calendar" element={<Calendar />} />
              <Route path="/pole-auto/materiel" element={<GestionMateriel />} />
              <Route
                path="/pole-auto/application"
                element={<ApplicationGarage />}
              />
              <Route path="/pole-auto/clients" element={<ClientForm />} />
              <Route
                path="/pole-auto/interventions"
                element={<InterventionForm />}
              />
              <Route
                path="/pole-auto/interventions/form"
                element={<InterventionForm />}
              />
              <Route
                path="/pole-auto/intervention"
                element={<InterventionForm />}
              />
              <Route
                path="/pole-auto/emargement"
                element={<EmargementCQP />}
              />
              <Route
                path="/pole-auto/toutes-interventions"
                element={<ToutesFormations />}
              />
              <Route
                path="/pole-auto/statistiques-clients"
                element={<StatistiquesClients />}
              />
              <Route path="/pole-auto/interventions-list"
              element={<InterventionsList />} />
            </Route>

            {/* Routes Admin - PROTÉGÉES */}
            <Route element={<AdminProtectedRoute />}>
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<DashboardAdmin />} />
                <Route path="articles" element={<ArticlesPage />} />
                <Route path="competences" element={<CompetencesPage />} />
                <Route path="formations" element={<FormationsPage />} />
                <Route path="apprenants" element={<ApprenantsPage />} />
                <Route path="statistiques" element={<StatistiquesPage />} />
                <Route path="roles" element={<RolesPage />} />
                <Route path="parametres" element={<ParametresPage />} />
                <Route path="pdfs" element={<PDFViewer />} />
                <Route path="interventions-pedagogiques" element={<ToutesFormations />} />
                <Route path="statistiques-clients" element={<StatistiquesClients />} />
              </Route>
            </Route>

            {/* Route 404 */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>

      <Footer />
      <CookieConsent />
    </div>
  );
}

// Composant principal qui choisit le bon layout
function AppContent() {
  console.log("🎯 AppContent rendu");
  
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* Route standalone SANS Header/Nav/Footer */}
        <Route path="/actualites-cfa" element={<StandaloneActualites />} />
        
        {/* Toutes les autres routes AVEC Header/Nav/Footer */}
        <Route path="*" element={<AppContentWithLayout />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  console.log("🎯 App rendu");
  
  return (
    <ToastProvider>
      <ErrorBoundary>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ErrorBoundary>
    </ToastProvider>
  );
}

export default App;